import Tool from './Tool';
import { zoomAndPanTo } from '../utils/zoom';
import { isFrame, isSimpleGroup, isSimpleSelection } from '../objects/guards';
import type Canvas from '../objects/Canvas';
import type Frame from '../objects/Frame';
import type WhiteboardController from '../controllers/WhiteboardController';
import { type IPoint } from 'fabric/fabric-impl';

const CURRENT_FRAME_DISTANCE_THRESHOLD = 200;

class ToolFrameBase extends Tool {
  // Return the frame closest to the center of the screen.
  static closestToCenter(allFrames: Frame[], canvas: Canvas): Frame {
    return allFrames.slice().sort((f1, f2) => {
      const d1 = this.distanceFromCenter(f1, canvas);
      const d2 = this.distanceFromCenter(f2, canvas);
      return d1 - d2;
    })[0];
  }

  // Find the distance from the frame to the center of the screen.
  static distanceFromCenter(frame: Frame, canvas: Canvas): number {
    const centerPoint = canvas.getVpCenter();
    return this.distanceFromPoint(frame, centerPoint);
  }

  // Find the distance from the frame to a point
  static distanceFromPoint(
    frame: Frame,
    point: IPoint,
    origin: 'tl' | 'center' = 'center'
  ): number {
    const d = frame.absolutePosition();
    const p =
      origin === 'center'
        ? { x: d.left + d.width / 2, y: d.top + d.height / 2 }
        : { x: d.left, y: d.top };
    return Math.hypot(point.x - p.x, point.y - p.y);
  }

  // Return the frame closest to the top left of the whiteboard
  static findFirstFrame(allFrames: Frame[]): Frame {
    // The furthest top/left point
    const point = { x: -Number.MAX_SAFE_INTEGER, y: -Number.MAX_SAFE_INTEGER };
    return allFrames.slice().sort((f1, f2) => {
      const d1 = this.distanceFromPoint(f1, point, 'tl');
      const d2 = this.distanceFromPoint(f2, point, 'tl');
      return d1 - d2;
    })[0];
  }

  // Frames ordered left to right, top to bottom.
  static orderedFrames(canvas: Canvas) {
    return this.allFrames(canvas).sort((f1, f2) => {
      const tl1 = f1.absolutePosition();
      const tl2 = f2.absolutePosition();
      const rowHeight = (tl1.height + tl1.height) / 2;

      if (Math.abs(tl1.top - tl2.top) < rowHeight * 0.75) {
        // Tops are close, so frames are in the same row, just use the lefts.
        return tl1.left - tl2.left;
      } else {
        // Tops are not close, so order by top only to get rows
        return tl1.top - tl2.top;
      }
    });
  }

  static anyFrame(canvas: Canvas) {
    return this.allFrames(canvas).length > 0;
  }

  static findFrame(
    parentCollection: Canvas | fabric.Object,
    frameId: string
  ): Frame | undefined {
    // breadth-first search for matching frame - we check all direct children before
    // descending into the hierarchy
    const collections = [parentCollection];
    for (const collection of collections) {
      for (const obj of collection.getObjects()) {
        if (isFrame(obj) && obj.uniqueId() === frameId) {
          return obj;
        } else if (isSimpleGroup(obj) || isSimpleSelection(obj)) {
          collections.push(obj);
        }
      }
    }
  }

  // Find the frame closest to the center of the screen, then find the next one by
  // moving left to right, top to bottom (vice versa for previous)
  static findNextOrPreviousFrame(canvas: Canvas, which: 'next' | 'previous') {
    const allFrames = this.orderedFrames(canvas);

    if (allFrames.length === 1) {
      return allFrames[0];
    }

    // Find the frame closest to the middle of the screen.
    const centerFrame = this.closestToCenter(allFrames, canvas);
    const distance = this.distanceFromCenter(centerFrame, canvas);
    // If it's way off from the center we'll assume it's not "current", and instead use the first/top-left frame
    if (distance > CURRENT_FRAME_DISTANCE_THRESHOLD) {
      return this.findFirstFrame(allFrames);
    }
    const onScreenIndex = allFrames.indexOf(centerFrame);
    if (onScreenIndex === -1) {
      throw new Error("Shouldn't happen");
    }

    const frameCount = allFrames.length;

    // increment/decrement the index, looping around to the start/end as needed
    const frameIndex =
      (onScreenIndex + frameCount + (which === 'next' ? 1 : -1)) % frameCount;

    return allFrames[frameIndex];
  }

  static framesInCollection(collection: Canvas | fabric.Object): Frame[] {
    return collection
      .getObjects()
      .map(obj => {
        if (isFrame(obj)) {
          return obj;
        } else if (isSimpleGroup(obj) || isSimpleSelection(obj)) {
          return this.framesInCollection(obj);
        } else {
          return [];
        }
      })
      .flat();
  }

  // All frames on the canvas, including those in groups.
  static allFrames(canvas: Canvas) {
    return this.framesInCollection(canvas).filter(obj => obj);
  }

  static zoomToFrame(frame: Frame, controller: WhiteboardController) {
    zoomAndPanTo(controller, [frame], {
      marginFactor: 0.9,
    });
  }
}

export default ToolFrameBase;
